import React, { useEffect } from 'react';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import TooltipWrapper, { TTPosition } from '../../general-components/tooltips/tooltip-wrapper';
import ZoomLogo from '../../../images/zoom-logo.svg';
import MicrosoftTeamsLogo from '../../../images/microsoft-teams-logo.svg';
import WebexLogo from '../../../images/webex-logo.svg';
import { Pages } from '../setup-conference-room';
import { EntryCodeContainer, CodeBox } from './elements';

interface Props {
    setPage: (page: number) => void;
    setSIPType: (sipType: string) => void;
    setEntryCode: (code: string) => void;
    initialCode?: string;
}

export default function EntryCode({
	setPage,
	setSIPType,
	setEntryCode,
	initialCode,
}: Props) {
	// Add useEffect to set initial values
	useEffect(() => {
		if (initialCode) {
			const codeArray = initialCode.split('');
			for (let i = 0; i < 5; i += 1) {
				const element = document.getElementById(`passcode-input-${i}`) as HTMLInputElement;
				if (element) {
					element.value = codeArray[i] || '';
				}
			}
		}
	}, [initialCode]);

	const onClick = (option: 'zoom' | 'teams' | 'webex' | 'other') => {
		setSIPType(option);
		setPage(Pages.SIPConfig);
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>, digit: number) => {
		e.target.value = e.target.value.toUpperCase();
		if (e.target.value.length > 0 && digit < 4) {
			document.getElementById(`passcode-input-${digit + 1}`)?.focus();
		}
	};

	const handleSubmit = async () => {
		const code = [
			(document.getElementById('passcode-input-0') as HTMLInputElement).value,
			(document.getElementById('passcode-input-1') as HTMLInputElement).value,
			(document.getElementById('passcode-input-2') as HTMLInputElement).value,
			(document.getElementById('passcode-input-3') as HTMLInputElement).value,
			(document.getElementById('passcode-input-4') as HTMLInputElement).value,
		].join('');

		setEntryCode(code);
		setPage(Pages.ViewConfig);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, digit: number) => {
		if (e.key === 'Delete') {
			if (e.currentTarget.value) {
				e.currentTarget.value = '';
			} else if (digit < 4) {
				const element = document.getElementById(`passcode-input-${digit + 1}`) as HTMLInputElement;
				element.value = '';
				element.focus();
			}
		} else if (e.key === 'Backspace') {
			if (e.currentTarget.value) {
				e.currentTarget.value = '';
			} else if (digit > 0) {
				const element = document.getElementById(`passcode-input-${digit - 1}`) as HTMLInputElement;
				element.value = '';
				element.focus();
			}
		} else if (e.key === 'Enter') {
			// Submit the code if the user presses Enter
			handleSubmit();
		} else if (e.key === 'ArrowRight' && digit < 4) {
			// Move focus when using arrow keys
			document.getElementById(`passcode-input-${digit + 1}`)?.focus();
		} else if (e.key === 'ArrowLeft' && digit > 0) {
			document.getElementById(`passcode-input-${digit - 1}`)?.focus();
		} else if (e.key.length > 1 || !e.key.match(/[A-Z0-9a-z]/) || !e.currentTarget.value) {
			// do not overwrite the value if the key is not a letter or number
		} else {
			// overwrite the value with the new key
			e.currentTarget.value = e.key.toUpperCase();
			if (digit < 4) {
				setTimeout(() => {
					// timeout to allow the value to be set before moving focus
					document.getElementById(`passcode-input-${digit + 1}`)?.focus();
				}, 0);
			}
		}
	};

	const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const paste = e.clipboardData.getData('text').toUpperCase().split('').filter((c) => c.match(/[A-Z0-9]/));
		for (let i = 0; i < 5; i += 1) {
			const value = paste[i] || '';
			const element = document.getElementById(`passcode-input-${i}`) as HTMLInputElement;
			if (element) {
				element.value = value;
			}
		}
	};

	return (
        <EntryCodeContainer>
            <div className="purple-gradient-2" />
            <div className="setup-conference-room-header">
                <h1>Greenroom for conference rooms</h1>
            </div>

            <div className="setup-conference-room-sub-header">
                <h2>
                    Enter the code displayed on your TV
                </h2>
                <TooltipWrapper message="Currently available for Cisco conference room solutions" position={TTPosition.right}>
                    <div className="info-i-tooltip">
                        <Icon name={ICONS.INFO_CIRCLE} size={12} color={COLORS.WHITE} />
                    </div>
                </TooltipWrapper>
            </div>

            <div className="connection-passcode-container">
                <CodeBox id="passcode-input-0" maxLength={1} onChange={(e) => onChange(e, 0)} onKeyDown={(e) => onKeyDown(e, 0)} onPaste={onPaste}/>
                <CodeBox id="passcode-input-1" maxLength={1} onChange={(e) => onChange(e, 1)} onKeyDown={(e) => onKeyDown(e, 1)} onPaste={onPaste}/>
                <CodeBox id="passcode-input-2" maxLength={1} onChange={(e) => onChange(e, 2)} onKeyDown={(e) => onKeyDown(e, 2)} onPaste={onPaste}/>
                <CodeBox id="passcode-input-3" maxLength={1} onChange={(e) => onChange(e, 3)} onKeyDown={(e) => onKeyDown(e, 3)} onPaste={onPaste}/>
                <CodeBox id="passcode-input-4" maxLength={1} onChange={(e) => onChange(e, 4)} onKeyDown={(e) => onKeyDown(e, 4)} onPaste={onPaste}/>
            </div>

            <div className='tab-container'>
                <div className='button-container'>
                    <button
                        className="next background-green font-weight-heavy"
                        onClick={handleSubmit}
                    >
                        Continue
                    </button>
                </div>
            </div>

            <div className="join-options-header">
                Or join via SIP:
            </div>
            <div className="sip-join-options">
                <div className="sip-join-option" onClick={() => onClick('zoom')}>
                    <img alt="Zoom" src={ZoomLogo} />
                    Zoom
                </div>
                <div className="sip-join-option" onClick={() => onClick('teams')}>
                    <img alt="Teams" src={MicrosoftTeamsLogo} />
                    Teams
                </div>
                <div className="sip-join-option" onClick={() => onClick('webex')}>
                    <img alt="Webex" src={WebexLogo} />
                    Webex
                </div>
                <div className="sip-join-option" onClick={() => onClick('other')}>
                    Other
                </div>
            </div>
        </EntryCodeContainer>
	);
}
